import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { filterBoolean, WorkerPool } from '@recruitee/common';
import { HttpApiService } from '@recruitee/http';
import {
  OfferBrief,
  OfferData,
  OfferFull,
  OfferItem,
  Position,
  SocialMediaData,
} from '@recruitee/http/types';

import { OfferScope } from '../../types';

@Injectable({
  providedIn: 'root',
})
export class JobsHttpService {
  constructor(private httpApi: HttpApiService, private workerPool: WorkerPool) {}

  public fetchCollection$(scope: OfferScope): Observable<{ offers: OfferItem[] }> {
    return this.httpApi.get(`/offers`, { kind: 'job', scope }).pipe(
      filterBoolean,
      switchMap(data =>
        data.references
          ? this.workerPool.fillInReferences(data.offers, data.references)
          : of(data.offers),
      ),
      map(offers => ({ offers })),
    );
  }

  public fetchBriefCollection$(scope: OfferScope): Observable<{ offers: OfferBrief[] }> {
    return this.httpApi
      .get('/offers', { kind: 'job', scope, viewMode: 'brief' })
      .pipe(filterBoolean);
  }

  public fetchItem$(params: {
    id: number | string;
    langCode?: string;
  }): Observable<{ offer: OfferFull }> {
    const body = { lang_code: params.langCode };
    return this.httpApi.get(`/offers/${params.id}`, body).pipe(filterBoolean);
  }

  public create$(data: { offer: OfferData }): Observable<{ offer: OfferFull }> {
    return this.httpApi.post('/offers/', null, data);
  }

  public createFromRequisition$(requisitionId: number): Observable<{ offer: OfferFull }> {
    return this.httpApi.post('/offers/', null, { requisitionId });
  }

  public update$(data: { offer: OfferData }): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${data.offer.id}`, null, data);
  }

  public delete$(params: { id: number }): Observable<{ offer: OfferFull }> {
    return this.httpApi.delete(`/offers/${params.id}`);
  }

  public follow(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/follow`);
  }

  public unfollow(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/unfollow`);
  }

  public duplicate$(id: number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/duplicate`);
  }

  public close(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/close`);
  }

  public archive(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/archive`);
  }

  public unarchive(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/unarchive`);
  }

  public publish(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/publish`);
  }

  public unpublish(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/unpublish`);
  }

  public draft(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/draft`);
  }

  public updatePositions$({ positions }: { positions: Position[] }): Observable<Position[]> {
    return this.httpApi
      .patch(`/offers/update_positions`, null, {
        offers: positions,
      })
      .pipe(map(() => positions));
  }

  public preview$({ data }: { data: OfferData }): Observable<{ previewUrl: string }> {
    let request;
    const body = { offer: data };

    if (data.id) {
      request = this.httpApi.patch(`/offers/${data.id}/preview`, null, body);
    } else {
      request = this.httpApi.post(`/offers/preview`, null, body);
    }

    return request;
  }

  public updateSocialMedia$(
    id: number,
    props: { langCode: string } & SocialMediaData,
  ): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/save_sharing_info`, null, { offer: props });
  }

  public updateSocialMediaImage$(id: number, file: File): Observable<{ id: number; url: string }> {
    const formData = new FormData();
    formData.append('sharing_image_upload', file);
    return this.httpApi.patch(`/offers/${id}/upload_sharing_image`, null, formData).pipe(
      map(response => ({
        ...response,
        id,
      })),
    );
  }
}
